import {EditorSelection, type Extension, StateEffect, StateField} from '@codemirror/state'
import {EditorView} from '@codemirror/view'

import {findWordBoundaries} from '../utils'

/**
 * Extension to fix double-click selection behavior when clicking near links or other elements
 * that expand on activation.
 *
 * The problem: When double-clicking text adjacent to a link, the first click triggers active node
 * updates that cause visual layout shifts (e.g., link delimiters appearing/disappearing via CSS).
 * While the document positions don't change, these visual changes can confuse the browser's native
 * double-click word selection logic, causing incorrect text to be selected.
 *
 * The solution: Track click positions at the mousedown level (before active node updates) and use
 * that position to calculate word boundaries on double-click.
 */

interface ClickState {
  position: number
  timestamp: number
}

// Double-click detection window (ms)
const DOUBLE_CLICK_TIME_WINDOW = 500

/**
 * Effect to update the click state.
 */
const updateClickStateEffect = StateEffect.define<ClickState | null>()

/**
 * StateField that tracks the last click state.
 */
const clickStateField = StateField.define<ClickState | null>({
  create: () => null,
  update: (state, tr) => {
    for (const effect of tr.effects) {
      if (effect.is(updateClickStateEffect)) {
        return effect.value
      }
    }
    return state
  },
})

/**
 * Event handlers for tracking clicks and handling double-clicks.
 */
const doubleClickHandler = EditorView.domEventHandlers({
  mousedown: (event, view) => {
    // Only track left clicks
    if (event.button !== 0) return false

    const now = Date.now()
    const pos = view.posAtCoords({x: event.clientX, y: event.clientY})
    if (pos === null) return false

    const lastClickState = view.state.field(clickStateField)

    // Check if this is the second mousedown in a double-click sequence
    if (lastClickState && now - lastClickState.timestamp < DOUBLE_CLICK_TIME_WINDOW) {
      // This is the second click - prevent default to stop incorrect native selection
      event.preventDefault()
      event.stopPropagation()
      // Don't update click state - keep the first click position
      return false
    }

    // This is a first click - store this position (captured before any active node updates)
    view.dispatch({
      effects: updateClickStateEffect.of({
        position: pos,
        timestamp: now,
      }),
    })

    return false
  },

  dblclick: (event, view) => {
    // Handle double-click selection using the stored first click position
    const lastClickState = view.state.field(clickStateField)

    if (!lastClickState) {
      // No stored click state from the first mousedown
      return false
    }

    // Prevent the default double-click behavior
    event.preventDefault()
    event.stopPropagation()

    const firstClickPos = lastClickState.position
    const text = view.state.doc.toString()

    // Calculate word boundaries at the first click position
    const {start, end} = findWordBoundaries(text, firstClickPos)

    if (start !== end) {
      // Set the selection
      view.dispatch({
        selection: EditorSelection.range(start, end),
        effects: [updateClickStateEffect.of(null)],
        userEvent: 'select.pointer',
      })

      return true
    }

    // Reset click state
    view.dispatch({
      effects: updateClickStateEffect.of(null),
    })
    return false
  },
})

/**
 * Extension that improves double-click selection behavior by tracking click positions
 * and fixing selection after layout shifts caused by active node changes.
 */
export const doubleClickSelectionExtension: Extension = [clickStateField, doubleClickHandler]
